import React, { useState, useRef, useCallback } from 'react'
import { Bell, FileText, Trash2, ArrowRight } from 'lucide-react'
import type { Todo } from '../../api/types'
import { useTodoActions } from '../../hooks/useTodoActions'
import { useSwipe } from '../../hooks/useSwipe'
import { useLongPress } from '../../hooks/useLongPress'
import { todayStr } from '../../lib/parse'
import { MobileTodoActionSheet } from './MobileTodoActionSheet'

interface MobileTodoItemProps {
  todo: Todo
}

export function MobileTodoItem({ todo }: MobileTodoItemProps) {
  const [sheetOpen, setSheetOpen] = useState(false)
  const [editing, setEditing] = useState(false)
  const [editText, setEditText] = useState(todo.text)
  const inputRef = useRef<HTMLInputElement>(null)

  const actions = useTodoActions(todo)

  const handleSwipeRight = useCallback(() => {
    if (todo.date) {
      actions.moveToNextDay()
    } else {
      actions.moveToDate(todayStr())
    }
  }, [todo.date, actions])

  const rowRef = useRef<HTMLDivElement>(null)
  const THRESHOLD: `${number}%` = '40%'
  const MAX_REVEAL: `${number}%` = '50%'
  const { bind: swipeBind, dx } = useSwipe({
    onSwipeLeft: actions.deleteWithUndo,
    onSwipeRight: handleSwipeRight,
    threshold: THRESHOLD,
    maxReveal: MAX_REVEAL,
  })

  // Convert dx (px) back to a fraction-of-threshold for opacity/scale ramps,
  // using the row's measured width. Falls back gracefully before first paint.
  const rowWidth = rowRef.current?.getBoundingClientRect().width ?? 0
  const thresholdPx = rowWidth * 0.4

  const longPress = useLongPress({
    onLongPress: () => setSheetOpen(true),
  })

  const enterEditMode = useCallback(() => {
    setEditing(true)
    setEditText(todo.text)
    setTimeout(() => {
      inputRef.current?.select()
    }, 0)
  }, [todo.text])

  const handleEditSave = useCallback(() => {
    actions.saveEditedText(editText)
    setEditing(false)
  }, [editText, actions])

  const handleEditKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleEditSave()
      } else if (e.key === 'Escape') {
        setEditing(false)
        setEditText(todo.text)
      }
    },
    [handleEditSave, todo.text]
  )

  const prio = todo.priority
  const priorityRowStyle: React.CSSProperties | undefined =
    prio == null
      ? undefined
      : {
          borderLeft: `3px solid var(--color-prio-${prio})`,
          background: `var(--color-prio-${prio}-bg)`,
        }

  const revealProgress = thresholdPx > 0 ? Math.min(1, Math.abs(dx) / thresholdPx) : 0
  const iconScale = 0.8 + 0.2 * revealProgress

  const combinedPointerDown = useCallback(
    (e: React.PointerEvent) => {
      swipeBind.onPointerDown(e)
      longPress.onPointerDown(e)
    },
    [swipeBind, longPress]
  )

  return (
    <>
      <div
        ref={rowRef}
        data-todo-row
        style={{
          position: 'relative',
          overflow: 'hidden',
          borderBottom: '1px solid var(--color-border)',
          touchAction: 'pan-y',
          ...priorityRowStyle,
        }}
        onPointerDown={combinedPointerDown}
        onPointerUp={longPress.onPointerUp}
        onPointerLeave={longPress.onPointerLeave}
        onPointerMove={longPress.onPointerMove}
        onPointerCancel={longPress.onPointerCancel}
      >
        {/* Reveal: delete (swipe left) */}
        {dx < 0 && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: '#B43A2C',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              paddingRight: 20,
              opacity: revealProgress,
            }}
          >
            <Trash2
              size={20}
              color="white"
              style={{ transform: `scale(${iconScale})` }}
            />
          </div>
        )}

        {/* Reveal: move to next day (swipe right) */}
        {dx > 0 && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'var(--color-accent)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              paddingLeft: 20,
              opacity: revealProgress,
            }}
          >
            <ArrowRight
              size={20}
              color="white"
              style={{ transform: `scale(${iconScale})` }}
            />
          </div>
        )}

        {/* Foreground row content */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 12px',
            transform: `translateX(${dx}px)`,
            background: 'var(--color-paper)',
            position: 'relative',
          }}
        >
          <button
            className={`status-circle ${todo.status === 'done' ? 'done' : ''}`}
            onClick={(e) => {
              e.stopPropagation()
              actions.toggleStatus()
            }}
            onPointerDown={(e) => e.stopPropagation()}
            title={`Status: ${todo.status.replace('_', ' ')}. Tap to cycle.`}
            style={{ marginTop: 2, flexShrink: 0 }}
          />

          <div style={{ flex: 1 }}>
            {editing ? (
              <input
                ref={inputRef}
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                onBlur={handleEditSave}
                onKeyDown={handleEditKeyDown}
                onPointerDown={(e) => e.stopPropagation()}
                autoFocus
                style={{
                  width: '100%',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '1px solid var(--color-accent)',
                  outline: 'none',
                  fontFamily: 'var(--font-serif)',
                  fontSize: 'var(--text-body)',
                  color: 'var(--color-ink)',
                  padding: 0,
                }}
              />
            ) : (
              <span
                onClick={enterEditMode}
                style={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: 'var(--text-body)',
                  lineHeight: 1.55,
                  fontWeight: 400,
                  color: todo.status === 'done' ? 'var(--color-ink-faint)' : 'var(--color-ink)',
                  display: 'block',
                  whiteSpace: 'normal',
                  overflowWrap: 'break-word',
                  cursor: 'text',
                }}
              >
                {todo.text}
              </span>
            )}
          </div>

          <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexShrink: 0 }}>
            {todo.reminder_at && (
              <span
                title={`Reminder: ${new Date(todo.reminder_at).toLocaleString()}`}
                style={{
                  color: 'var(--color-accent)',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Bell size={12} strokeWidth={1.5} />
              </span>
            )}
            {todo.note && (
              <span
                style={{
                  color: 'var(--color-ink-muted)',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <FileText size={12} strokeWidth={1.5} />
              </span>
            )}
          </div>
        </div>
      </div>

      <MobileTodoActionSheet
        todo={todo}
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        onRequestEdit={() => {
          setSheetOpen(false)
          setTimeout(() => enterEditMode(), 50)
        }}
      />
    </>
  )
}
